import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Box } from "@mui/material";

import useEntity from "../../hooks/useEntity";
import useLocalization from "../../hooks/useLocalization";
import usePage from "../../hooks/usePage";
import useData from "../../hooks/useData";
import useProject from "../../hooks/useProject";
import useSurvey from "../../hooks/useSurvey";

import Page from "./Page";
import Card from "../layout/Card";
import SurveyEditor from "../editors/SurveyEditor";
import Availability from "../ui/Availability";
import Visibility from "../ui/Visibility";
import Dialog_YesNo from "../ui/Dialog_YesNo";

import SurveyEntryModal from "../modals/SurveyEntryModal";
import SurveyCopyModal from "../modals/SurveyCopyModal";

const Survey = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getTitle, getMessage, getLabel } = useLocalization();
  const { executeAction } = useData();
  const { getProject, project } = useProject();
  const { getSurveyAnswerCount } = useSurvey();

  const [surveyId, setSurveyId] = useState(null);
  const [deleteSurvey, setDeleteSurvey] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openSurveyEntry, setOpenSurveyEntry] = useState(false);
  const [openSurveyCopy, setOpenSurveyCopy] = useState(false);
  const [hasCallResults, setHasCallResults] = useState(false);

  const {
    setEntityName,
    getEntityData,
    entityData,
    updateField,
    saveEntityData,
    setMessage,
    message,
    saved,
  } = useEntity();

  useEffect(() => {
    setEntityName("Survey");
    setSurveyId(location.state.surveyId);
    setProjectId(location.state.projectId);
    setClientId(location.state.clientId);

    getProject(location.state.projectId);
  }, []);

  useEffect(() => {
    if (!surveyId) return;

    getEntityData(surveyId);
  }, [surveyId]);

  useEffect(() => {
    if (!surveyId) return;

    const loadCallResultUsage = async () => {
      const results = await executeAction({
        entityName: "CallResult",
        action: "getRecordCount",
        filter: { surveyId },
      });

      if (results?.isSuccessful) {
        setHasCallResults((results.data?.recordCount || 0) > 0);
      } else {
        setHasCallResults(false);
      }
    };

    loadCallResultUsage();
  }, [surveyId]);

  useEffect(() => {}, [entityData]);

  useEffect(() => {
    if (!saved || !deleteSurvey) return;

    navigate("/project", {
      state: { projectId: projectId, clientId: clientId },
    });
  }, [saved, deleteSurvey]);

  const handleToolbarClick = async (name) => {
    switch (name) {
      case "project":
        navigate("/project", {
          state: { projectId, clientId: clientId },
        });
        break;

      case "design":
        navigate("/surveydesigner", {
          state: {
            surveyId: entityData.id,
            projectId: projectId,
            clientId: clientId,
          },
        });

      case "delete":
        setOpenDialog(true);
        break;

      case "mobile":
        setOpenSurveyEntry(true);
        break;

      case "orderDashboard":
        navigate("/orderdashboard");
        break;

      case "analysis":
        navigate("/surveyresults", {
          state: {
            surveyId: entityData.id,
            projectId,
            clientId,
          },
        });
        break;

      case "export":
        navigate("/survey-answer-export", {
          state: {
            surveyId: entityData.id,
            projectId,
            clientId,
          },
        });
        break;

      case "images":
        navigate("/surveyimages", {
          state: {
            surveyId: entityData.id,
            projectId,
            clientId,
          },
        });
        break;

      case "copy":
        setOpenSurveyCopy(true);
        break;
    }
  };

  const handleAvailabilityChange = async (newAvailability) => {
    await updateField("availability", newAvailability);
  };

  const handleSaveAvailability = async () => {
    await handleSave(entityData);
  };

  const handleVisibilityChange = async (newVisibility) => {
    await updateField("visibility", newVisibility);
  };

  const handleSaveVisibility = async () => {
    await handleSave(entityData);
  };

  const handleSave = async (data) => {
    let newData = { ...entityData, ...data };

    newData = transformData(newData);

    await saveEntityData(newData);
  };

  const handleCancelDelete = async () => {
    setOpenDialog(false);
  };

  const handleConfirmDelete = async () => {
    setOpenDialog(false);

    await executeAction({
      entityName: "Survey",
      action: "delete",
      data: { id: surveyId },
    });
    navigate("/project", {
      state: { projectId, clientId: clientId },
    });
  };

  const transformData = (data) => {
    // Check survey availability and remove entires with no dates
    let newAvailability = [];
    for (const item of entityData.availability) {
      if (!item.startsOn && !item.endsOn) {
        continue;
      }
      newAvailability.push(item);
    }

    let orderConfig = {};
    if (data.surveyType === "order") {
      if (!data.orderConfig) {
        orderConfig.onlyListedSkus = false;
      }
    }

    let newData = { ...data, availability: newAvailability, orderConfig };

    return newData;
  };

  const deleteMessage = async () => {
    const answers = await getSurveyAnswerCount(entityData.id);

    let message;
    if (answers.count > 0) {
      message = getMessage("confirmSurveyDeleteWithAnswers");
      message.replace("{answerCount}", answers.count.toString());
    } else {
      message = getMessage("confirmSurveyDeleteWithoutAnswers");
    }

    return message;
  };

  if (!entityData) return null;

  return (
    <>
      <Page
        entityName={entityData.name}
        pageName={project?.name}
        includeToolbar
        items={[
          { name: "project", iconName: "ListAlt" },
          {
            name: "design",
            iconName: "DesignServices",
            hide: entityData.surveyType === "order" ? true : false,
          },
          { name: "copy", iconName: "ContentCopy" },
          {
            name: "mobile",
            iconName: "Smartphone",
          },
          {
            name: "analysis",
            iconName: "Insights",
          },
          {
            name: "export",
            iconName: "Download",
          },
          {
            name: "images",
            iconName: "Collections",
          },
          { name: "orderDashboard", iconName: "ShoppingCart" },
          { name: "delete", iconName: "Delete", hide: hasCallResults },
        ]}
        onToolItemClick={handleToolbarClick}
      >
        <Box
          display="flex"
          gap={1}
        >
          <Card
            title={getTitle("surveyInformation")}
            sx={{ flex: 3 }}
          >
            <SurveyEditor
              data={entityData}
              onSave={handleSave}
              message={message}
            />
          </Card>

          <Card
            title={getTitle("availability")}
            sx={{ flex: 3 }}
          >
            <Availability
              value={
                Array.isArray(entityData.availability)
                  ? entityData.availability
                  : []
              }
              onChange={handleAvailabilityChange}
              onSave={handleSaveAvailability}
              width={400}
            />
          </Card>

          <Card
            sx={{ flex: 3 }}
            title={getTitle("visibility")}
          >
            <Visibility
              value={
                Array.isArray(entityData.visibility)
                  ? entityData.visibility
                  : []
              }
              onChange={handleVisibilityChange}
              onSave={handleSaveVisibility}
            />
          </Card>
        </Box>
      </Page>

      {openSurveyEntry ? (
        <SurveyEntryModal
          open={openSurveyEntry}
          onClose={() => setOpenSurveyEntry(false)}
          surveyId={surveyId}
          mode="test"
          layout="mobile"
        />
      ) : null}

      {openSurveyCopy ? (
        <SurveyCopyModal
          open={openSurveyCopy}
          onClose={() => setOpenSurveyCopy(false)}
          surveyId={surveyId}
          onSave={async () => {
            setOpenSurveyCopy(false);
            await getEntityData(surveyId);
          }}
        />
      ) : null}

      {openDialog && (
        <Dialog_YesNo
          open={openDialog}
          onClose={handleCancelDelete}
          title={getTitle("confirmSurveyDelete")}
          message={deleteMessage()}
          onYes={handleConfirmDelete}
          onNo={handleCancelDelete}
        />
      )}
    </>
  );
};

export default Survey;
